import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { FC, useEffect, useState } from 'react'

import { CCEmailsSection } from 'components/common/CCEmailsSection'
import { Flow } from 'constants/misc'
import { logger } from 'utils/logging'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useTranslation } from 'react-i18next'
import { useUpdateWorkspaceCcEmails } from 'dataQueries'
import { useUserData } from 'components/contexts/UserDataContext'

/**
 * @component Workspace CC Emails settings displayed in Workspace Information page
 * @example
 * <WorkspaceCCEmailsModule />
 */
export const WorkspaceCCEmailsModule: FC = () => {
  const { t } = useTranslation(['workspace_settings'])
  const { userPropertyWorkspaceId, currentWorkspaceCcEmails } = useUserData()
  const { spawnSuccessToast, spawnErrorToast } = useSnackbar()

  const updateWorkspaceCcEmails = useUpdateWorkspaceCcEmails()

  const [optimisticEmails, setOptimisticEmails] = useState<string[] | null>(null)
  const displayedEmails = optimisticEmails ?? currentWorkspaceCcEmails

  useEffect(() => {
    setOptimisticEmails(null)
  }, [currentWorkspaceCcEmails])

  const mutateEmails = (emails: string[], analyticsEvent: AnalyticsEvent) => {
    logAnalyticsEvent(analyticsEvent, { Flow: Flow.WORKSPACE_SETTINGS })
    updateWorkspaceCcEmails.mutate(
      { workspaceId: userPropertyWorkspaceId, ccEmails: emails },
      {
        onSuccess: () => {
          setOptimisticEmails(emails)
          spawnSuccessToast(t('workspace_settings:cc_emails.update_success'))
        },
        onError: (error) => {
          logger.error('Failed to update workspace CC emails', error, { context: { component: `WorkspaceCCEmailsModule ${userPropertyWorkspaceId}` } })
          spawnErrorToast(t('workspace_settings:cc_emails.update_error'))
        },
      }
    )
  }

  return (
    <CCEmailsSection
      title={t('workspace_settings:cc_emails.title')}
      description={t('workspace_settings:cc_emails.description')}
      inputLabel={t('workspace_settings:cc_emails.input_label')}
      ccEmails={displayedEmails}
      isPending={updateWorkspaceCcEmails.isPending}
      onAdd={(emails) => mutateEmails(emails, AnalyticsEvent.CC_EMAIL_ADDED)}
      onDelete={(email) => mutateEmails(displayedEmails.filter((prevEmail) => prevEmail !== email), AnalyticsEvent.CC_EMAIL_REMOVED)}
    />
  )
}
